import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  getStoreNotificationsAsync,
  getStoreNotificationUnreadCountAsync,
  viewAllStoreNotificationsAsync,
  viewStoreNotificationAsync,
} from "./notifications.async";

const initialState = {
  notificationLoader: false,
  notificationUpdateLoader: false,
  notifications: [],
  unreadCount: 0,
};

const getNotificationId = (notification) =>
  notification?.id ?? notification?._id ?? notification?.notificationId;

const normalizeNotifications = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.list)) return payload.data.list;
  if (Array.isArray(payload?.notifications)) return payload.notifications;
  if (Array.isArray(payload?.data?.notifications)) {
    return payload.data.notifications;
  }
  return [];
};

const normalizeUnreadCount = (payload) => {
  const count =
    payload?.unreadCount ??
    payload?.count ??
    payload?.data?.unreadCount ??
    payload?.data?.count ??
    payload?.data;

  return Number.isFinite(Number(count)) ? Number(count) : 0;
};

const upsertNotification = (notifications, newNotification) => {
  const notificationId = getNotificationId(newNotification);

  if (!notificationId) {
    return [newNotification, ...notifications];
  }

  const currentIndex = notifications.findIndex(
    (notification) => getNotificationId(notification) === notificationId
  );

  if (currentIndex === -1) {
    return [newNotification, ...notifications];
  }

  const nextNotifications = [...notifications];
  nextNotifications[currentIndex] = {
    ...nextNotifications[currentIndex],
    ...newNotification,
  };
  return nextNotifications;
};

export const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    receiveStoreNotification: (state, action) => {
      state.notifications = upsertNotification(
        state.notifications,
        action.payload
      );
      state.unreadCount += 1;
    },
    emptyStoreNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        getStoreNotificationsAsync.pending,
        getStoreNotificationUnreadCountAsync.pending
      ),
      (state) => {
        state.notificationLoader = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        viewStoreNotificationAsync.pending,
        viewAllStoreNotificationsAsync.pending
      ),
      (state) => {
        state.notificationUpdateLoader = true;
      }
    );

    builder.addMatcher(
      isAnyOf(getStoreNotificationsAsync.fulfilled),
      (state, action) => {
        state.notificationLoader = false;
        state.notifications = normalizeNotifications(action.payload);
      }
    );

    builder.addMatcher(
      isAnyOf(getStoreNotificationUnreadCountAsync.fulfilled),
      (state, action) => {
        state.notificationLoader = false;
        state.unreadCount = normalizeUnreadCount(action.payload);
      }
    );

    builder.addMatcher(
      isAnyOf(viewStoreNotificationAsync.fulfilled),
      (state, action) => {
        const notificationId =
          action.meta.arg?.id ?? getNotificationId(action.payload?.data);
        const notification = state.notifications.find(
          (item) => getNotificationId(item) === notificationId
        );

        if (notification && !notification.isViewed) {
          state.unreadCount = Math.max(state.unreadCount - 1, 0);
        }

        if (notification) {
          notification.isViewed = true;
        }

        state.notificationUpdateLoader = false;
      }
    );

    builder.addMatcher(
      isAnyOf(viewAllStoreNotificationsAsync.fulfilled),
      (state) => {
        state.notifications = state.notifications.map((notification) => ({
          ...notification,
          isViewed: true,
        }));
        state.unreadCount = 0;
        state.notificationUpdateLoader = false;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getStoreNotificationsAsync.rejected,
        getStoreNotificationUnreadCountAsync.rejected
      ),
      (state) => {
        state.notificationLoader = false;
      }
    );

    builder.addMatcher(
      isAnyOf(
        viewStoreNotificationAsync.rejected,
        viewAllStoreNotificationsAsync.rejected
      ),
      (state) => {
        state.notificationUpdateLoader = false;
      }
    );
  },
});

export const { emptyStoreNotifications, receiveStoreNotification } =
  notificationsSlice.actions;

export default notificationsSlice.reducer;
